/**
 * Ideas Service
 * Handles all idea-related data operations with real API calls
 */

import { Idea, Category, IdeasResponse } from "../types";
import {
  searchIdeas,
  searchIdeasByUser,
  getIdeaById as apiGetIdeaById,
  searchIdeasByQuery as apiSearchIdeasByQuery,
  postIdea,
  updateIdea,
  deleteIdea,
  saveSuggestion,
  getFollowingIdeas,
  type SearchIdeasParams,
  type SearchIdeasByUserParams,
  type PostIdeaParams,
  type UpdateIdeaParams,
  type SaveSuggestionParams,
  type FollowingIdeasParams,
} from "../api/ideas.api";
import { uploadMedia } from "@/api/s3";

export const getTrendingIdeas = async (
  page?: string,
  user?: any,
): Promise<IdeasResponse> => {
  try {
    const params: SearchIdeasParams = {
      query: "",
      page: page || "0",
      user
    };

    const response = await searchIdeas(params);

    if (response.status === 200 && response.data) {
      const ideasArray = Array.isArray(response.data)
        ? response.data : [];

      // Safely extract the next page token from response.page or response.nextPageToken
      const rawNextPage = response.page ?? response.nextPageToken ?? response.nextPage;

      return {
        ideas: ideasArray,
        hasMore: response.hasMore ?? (!!rawNextPage),
        nextPage: rawNextPage,
        total: response.total || ideasArray.length,
      };
    } else {
      return {
        ideas: [],
        hasMore: false,
        total: 0,
      };
    }
  } catch (error) {
    console.error("Get trending ideas error:", error);
    return {
      ideas: [],
      hasMore: false,
      total: 0,
    };
  }
};

export const getCategories = async (): Promise<Category[]> => {
  try {
    // For now, return a static list since the API doesn't have a categories endpoint
    // This can be updated when the API provides categories
    return [
      { id: "1", key: "other", name: "All Ideas" },
      { id: "2", key: "ai", name: "AI" },
      { id: "3", key: "edtech", name: "EdTech" },
      { id: "4", key: "fintech", name: "Fintech" },
      { id: "5", key: "gaming", name: "Gaming" },
      { id: "6", key: "healthtech", name: "HealthTech" },
      { id: "7", key: "saas", name: "SaaS" },
      { id: "8", key: "climate", name: "Climate" },
      { id: "9", key: "media", name: "Media" },
      { id: "10", key: "ecommerce", name: "E-commerce" },
      { id: "11", key: "deeptech", name: "DeepTech" },
    ];
  } catch (error) {
    console.error("Get categories error:", error);
    return [];
  }
};

export const getIdeasByUser = async (
  username: string,
): Promise<IdeasResponse> => {
  try {
    const params: SearchIdeasByUserParams = {
      username,
      page: "0",
    };

    const response = await searchIdeasByUser(params);

    if (response.status === 200 && response.data) {
      const ideasArray = Array.isArray(response.data)
        ? response.data : [];

      return {
        ideas: ideasArray,
        hasMore: response.hasMore || false,
        nextPage: response.nextPage || response.page,
        total: response.total || ideasArray.length,
      };
    } else {
      return {
        ideas: [],
        hasMore: false,
        total: 0,
      };
    }
  } catch (error) {
    console.error("Get ideas by user error:", error);
    return {
      ideas: [],
      hasMore: false,
      total: 0,
    };
  }
};

export const searchIdeasByQuery = async (params: {
  query: string;
  page?: string;
  limit?: number;
  user?: any;
}): Promise<IdeasResponse> => {
  try {
    const searchParams: SearchIdeasParams = {
      query: params.query,
      page: params.page || "0",
      user: params.user,
    };

    const response = await apiSearchIdeasByQuery(searchParams);

    if (response.status === 200 && response.data) {
      const ideasArray = Array.isArray(response.data)
        ? response.data : [];

      return {
        ideas: ideasArray,
        hasMore: response.hasMore || false,
        nextPage: response.nextPage || response.page,
        total: response.total || ideasArray.length,
      };
    } else {
      return {
        ideas: [],
        hasMore: false,
        total: 0,
      };
    }
  } catch (error) {
    console.error("Search ideas error:", error);
    return {
      ideas: [],
      hasMore: false,
      total: 0,
    };
  }
};

export const getIdeaById = async (id: string): Promise<Idea | null> => {
  try {
    const response = await apiGetIdeaById(id);

    if (response.status === 200 && response.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Get idea by ID error:", error);
    return null;
  }
};

export const createIdea = async (
  params: PostIdeaParams,
): Promise<{
  success: boolean; idea?: Idea; message?: string
}> => {
  try {
    const response = await postIdea(params);

    if (response.status === 200 && response.data) {
      return {
        success: true,
        idea: response.data,
      };
    } else {
      return {
        success: false,
        message: response.error || "Failed to create idea",
      };
    }
  } catch (error) {
    console.error("Create idea error:", error);
    return {
      success: false,
      message: "An unexpected error occurred",
    };
  }
};

export const postIdeaMedia = async (media: any): Promise<{
  copyrightUri: string | null;
  mediaUri: string | null;
  mediaUris: string[];
  success: boolean;
}> => {
  // Helper to convert Base64 Data URI to a Blob
  const base64ToBlob = (dataURI: string) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  let mediaUri = null;
  const mediaUris: string[] = [];
  let copyrightUri = null;

  try {
    // 1. Process Main Media
    if (media.uris && Array.isArray(media.uris) && media.uris.length > 0) {
      for (let index = 0; index < media.uris.length; index++) {
        const currentUri = media.uris[index];
        const isBase64 = currentUri.startsWith('data:');
        const mimeType = isBase64 ? currentUri.split(',')[0].split(':')[1].split(';')[0] : media.type;
        let blob;

        if (isBase64) {
          blob = base64ToBlob(currentUri);
        } else {
          const response = await fetch(currentUri);
          blob = await response.blob();
        }

        const extension = mimeType.split('/')[1] || 'bin';
        const actualFileName = `media_${index}.${extension}`;

        const uploadedUri = await uploadMedia("idfy-ideas", {
          type: mimeType,
          file: new File([blob], actualFileName, { type: mimeType }),
        }, media.id || "idea_media");
        
        if (uploadedUri) {
          mediaUris.push(uploadedUri);
        }
      }
      if (mediaUris.length > 0) {
        mediaUri = mediaUris[0]; // For backwards compatibility
      }
    } else if (media.uri) {
      const isBase64 = media.uri.startsWith('data:');
      const mimeType = isBase64 ? media.uri.split(',')[0].split(':')[1].split(';')[0] : media.type;
      let blob;

      if (isBase64) {
        blob = base64ToBlob(media.uri);
      } else {
        const response = await fetch(media.uri);
        blob = await response.blob();
      }

      const extension = mimeType.split('/')[1] || 'bin';
      const actualFileName = `media.${extension}`;

      mediaUri = await uploadMedia("idfy-ideas", {
        type: mimeType,
        file: new File([blob], actualFileName, { type: mimeType }),
      }, media.id || "idea_media");

      if (mediaUri) {
        mediaUris.push(mediaUri);
      }
    }

    // 2. Process Copyright Proof
    if (media.proof) {
      const isBase64 = media.proof.startsWith('data:');
      let proofBlob;

      if (isBase64) {
        proofBlob = base64ToBlob(media.proof);
      } else {
        const response = await fetch(media.proof);
        proofBlob = await response.blob();
      }

      const proofExtension = media.proofType.split('/')[1] || 'bin';
      const proofFileName = `proof.${proofExtension}`;

      copyrightUri = await uploadMedia("idfy-copyright", {
        type: media.proofType,
        file: new File([proofBlob], proofFileName, { type: media.proofType }),
      }, media.id || "idea_media");
    }

    return { copyrightUri, mediaUri, mediaUris, success: true };

  } catch (error) {
    console.error("Upload Error:", error);
    throw error;
  }
};

export const postBannerMedia = async (media: any, id: string): Promise<{
  mediaUri: string | null;
  success: boolean;
}> => {
  // Helper to convert Base64 Data URI to a Blob
  const base64ToBlob = (dataURI: string) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  let mediaUri = null;

  try {
    // 1. Process Main Media
    if (media.uri) {
      const isBase64 = media.uri.startsWith('data:');
      const mimeType = isBase64 ? media.uri.split(',')[0].split(':')[1].split(';')[0] : media.type;
      let blob;

      if (isBase64) {
        blob = base64ToBlob(media.uri);
      } else {
        const response = await fetch(media.uri);
        blob = await response.blob();
      }

      mediaUri = await uploadMedia("idfy-banners", {
        type: mimeType,
        file: new File([blob], media.title || "image.jpg", { type: mimeType }),
      }, id);
    }

    return { mediaUri, success: true };

  } catch (error) {
    console.error("Upload Error:", error);
    throw error;
  }
};

export const postAvatarMedia = async (media: any, id: string): Promise<{
  mediaUri: string | null;
  success: boolean;
}> => {
  // Helper to convert Base64 Data URI to a Blob
  const base64ToBlob = (dataURI: string) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  let mediaUri = null;

  try {
    // 1. Process Main Media
    if (media.uri) {
      const isBase64 = media.uri.startsWith('data:');
      const mimeType = isBase64 ? media.uri.split(',')[0].split(':')[1].split(';')[0] : media.type;
      let blob;

      if (isBase64) {
        blob = base64ToBlob(media.uri);
      } else {
        const response = await fetch(media.uri);
        blob = await response.blob();
      }

      mediaUri = await uploadMedia("idfy-avatars", {
        type: mimeType,
        file: new File([blob], media.title || "image.jpg", { type: mimeType }),
      }, id);
    }

    return { mediaUri, success: true };

  } catch (error) {
    console.error("Upload Error:", error);
    throw error;
  }
};

export const updateIdeaById = async (
  id: string,
  params: Omit<UpdateIdeaParams, "id">,
): Promise<{ success: boolean; idea?: Idea; message?: string }> => {
  try {
    // The updateIdea API expects the id in the params object
    const updateParams = { ...params, id };
    const response = await updateIdea(updateParams);

    if (response.status === 200 && response.data) {
      return {
        success: true,
        idea: response.data,
      };
    } else {
      return {
        success: false,
        message: response.error || "Failed to update idea",
      };
    }
  } catch (error) {
    console.error("Update idea error:", error);
    return {
      success: false,
      message: "An unexpected error occurred",
    };
  }
};

export const deleteIdeaById = async (
  id: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    // The deleteIdea API requires a likes parameter, we'll use 0 as default
    const response = await deleteIdea(id, 0);

    if (response.status === 200) {
      return {
        success: true,
        message: "Idea deleted successfully",
      };
    } else {
      return {
        success: false,
        message: response.error || "Failed to delete idea",
      };
    }
  } catch (error) {
    console.error("Delete idea error:", error);
    return {
      success: false,
      message: "An unexpected error occurred",
    };
  }
};

export const saveSuggestionForIdea = async (
  params: SaveSuggestionParams,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await saveSuggestion(params);

    if (response.status === 200) {
      return {
        success: true,
        message: "Suggestion saved successfully",
      };
    } else {
      return {
        success: false,
        message: response.error || "Failed to save suggestion",
      };
    }
  } catch (error) {
    console.error("Save suggestion error:", error);
    return {
      success: false,
      message: "An unexpected error occurred",
    };
  }
};

export const getFollowingIdeasFeed = async (
  params: FollowingIdeasParams = {},
): Promise<IdeasResponse> => {
  try {
    const response = await getFollowingIdeas(params);

    console.log("Raw API Response:", response);

    if (response.status === 200 && response.data) {
      const ideasArray = Array.isArray(response.data)
        ? response.data : [];

      return {
        ideas: ideasArray,
        hasMore: response.hasMore || false,
        nextPage: response.nextPage || response.page,
        total: response.total || ideasArray.length,
      };
    } else {
      console.log("API Error Response:", response);
      return {
        ideas: [],
        hasMore: false,
        total: 0,
      };
    }
  } catch (error) {
    console.error("Get following ideas error:", error);
    return {
      ideas: [],
      hasMore: false,
      total: 0,
    };
  }
};
